import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SiteThemeControl from './SiteThemeControl';
import './SiteTheme.css';

export const THEME_KEY = 'magellan-theme';
export const THEME_OPTIONS = ['system', 'light', 'dark'];
export const SiteThemeContext = createContext(null);
export function initialPreference() {
  try { const value = localStorage.getItem(THEME_KEY); return THEME_OPTIONS.includes(value) ? value : 'system'; }
  catch { return 'system'; }
}
export function useThemePreference(active = true) {
  const [preference, setPreference] = useState(initialPreference);
  const [systemDark, setSystemDark] = useState(() => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true);
  const theme = preference === 'system' ? (systemDark ? 'dark' : 'light') : preference;
  useEffect(() => {
    if (!active) return undefined;
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    const change = () => setSystemDark(media.matches);
    const sync = event => { if (event.key === THEME_KEY || event.key === null) setPreference(initialPreference()); };
    media?.addEventListener('change', change);
    window.addEventListener('storage', sync);
    return () => { media?.removeEventListener('change', change); window.removeEventListener('storage', sync); };
  }, [active]);
  return { preference, theme, select(value) {
    if (!THEME_OPTIONS.includes(value)) return;
    setPreference(value);
    try { localStorage.setItem(THEME_KEY, value); } catch { /* Selection still applies for this visit. */ }
  } };
}
export function useSiteTheme() { return useContext(SiteThemeContext); }
export function applySiteTheme(theme) {
  const html = document.documentElement;
  html.dataset.siteTheme = theme;
  html.classList.toggle('dark', theme === 'dark');
  html.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'light' ? '#f2f5f8' : '#101418');
}
export default function SiteThemeProvider({ children }) {
  const state = useThemePreference();
  const { pathname } = useLocation();
  const [embedded, setEmbedded] = useState(0);
  // App shells (admin, customer account) show their own switch while mounted.
  const claimThemeControl = useCallback(() => {
    setEmbedded(n => n + 1);
    return () => setEmbedded(n => n - 1);
  }, []);
  const preview = ['/customer/identity-preview', '/customer/portal-preview', '/commerce/preview', '/customer/delivery-preview'].includes(pathname);
  const theme = preview ? 'dark' : state.theme;
  useLayoutEffect(() => { applySiteTheme(theme); }, [theme]);
  return <SiteThemeContext.Provider value={{ ...state, theme, themeForced: preview, claimThemeControl }}>
    {children}
    {!preview && !embedded && <SiteThemeControl preference={state.preference} select={state.select} />}
  </SiteThemeContext.Provider>;
}
